import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSales } from "@/contexts/SalesContext";
import AEPSetupModal from "@/pages/aep/AEPExportWizard";
import {
  SECTIONS, ORIENT_CLASS, MARGIN_CLASS, FONT_CLASS, MARGIN_MM,
} from "@/pages/aep/shared";
import { deriveProbabilityIndex, deriveSeverityIndex, matrixClassification } from "@/lib/aepContent";
import {
  AEPCover, AEPSummary, AEPDashboard, AEPControleDoc, AEPResumoExec,
  AEPPrioridades, AEPRastreabilidade, AEPHeatmap, AEPTimeline,
  AEPChecklist, AEPLgpd, AEPIntro, AEPBase, AEPConsolidacao,
  AEPMatriz, AEPMatrizConsolidada, AEPPlano, AEPPlanoAnual,
  AEPAbsenteismo, AEPRecomendacoesIA,
  AEPConclusao, AEPAssinaturas,
} from "@/pages/aep/AEPSections";

/**
 * Consolidated AEP — same layout / sections / setup modal / print flow as the
 * per-assessment AEP, but driven by `/api/cnpj/{cnpj}/aep` which aggregates
 * dimensions + questions + responses + action plans across every cadastro under
 * the CNPJ. Re-using AEPSections lets the user edit, sign and print this report
 * with the exact same UI muscle memory.
 */
export default function CnpjAEPReport() {
  const { cnpj } = useParams();
  const { user } = useAuth();
  const { whatsappUrl, mailtoUrl } = useSales();
  const isTrial = Boolean(user?.is_trial);
  // Filter by instrument via URL — persists across refresh + shareable link.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTestType = searchParams.get("test_type") || "";

  const [raw, setRaw] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [attachments, setAttachments] = useState({});
  const [showSetup, setShowSetup] = useState(false);
  const [orient, setOrient] = useState("retrato");
  const [margin, setMargin] = useState("normal");
  const [font, setFont] = useState("normal");
  // Hide capa-specific signature section by default (consolidado doesn't have
  // technical signers per cadastro). User can still enable it via setup modal.
  const [included, setIncluded] = useState(
    () => Object.fromEntries(SECTIONS.map((s) => [s.k, s.k !== "assinaturas"])),
  );
  // Quais cortes da Matriz Consolidada exibir (Geral · GHE · Setor · Individual)
  const [matrizViews, setMatrizViews] = useState(["geral", "ghe", "individual"]);
  // Editable signer identity persisted per CNPJ (via /aep-editor-config).
  // Overrides the default "Administrador Sollute" on the cover + signatures.
  const [signerConfig, setSignerConfig] = useState({ technical_responsible: "", company_representative: "" });

  useEffect(() => {
    let mounted = true;
    // Pass ?test_type=… to filter aggregation to a single instrument. When
    // omitted, backend returns mixed_instruments=true and the picker modal
    // is displayed until the user chooses one.
    const qs = activeTestType ? `?test_type=${encodeURIComponent(activeTestType)}` : "";
    api.get(`/cnpj/${cnpj}/aep${qs}`)
      .then((r) => { if (mounted) setRaw(r.data); })
      .catch((e) => toast.error(e.response?.data?.detail || e.message));
    api.get(`/cnpj/${cnpj}/aep-overrides`)
      .then((r) => { if (mounted) setOverrides(r.data || {}); })
      .catch(() => { /* non-blocking */ });
    api.get(`/cnpj/${cnpj}/aep-attachments`)
      .then((r) => { if (mounted) setAttachments(r.data || {}); })
      .catch(() => { /* non-blocking */ });
    api.get(`/cnpj/${cnpj}/aep-editor-config`)
      .then((r) => {
        if (!mounted) return;
        setSignerConfig({
          technical_responsible: r.data?.technical_responsible || "",
          company_representative: r.data?.company_representative || "",
        });
      })
      .catch(() => { /* non-blocking */ });
    return () => { mounted = false; };
  }, [cnpj, activeTestType]);

  // Save signer identity fields (technical_responsible / company_representative)
  // to the editor-config immediately on blur — silent, optimistic.
  const saveSignerField = async (field, value) => {
    const clean = (value || "").trim();
    setSignerConfig((s) => ({ ...s, [field]: clean }));
    try {
      await api.put(`/cnpj/${cnpj}/aep-editor-config`, { [field]: clean });
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const handleOverrideChange = (sectionKey, doc) => {
    setOverrides((prev) => {
      const next = { ...prev };
      if (doc) next[sectionKey] = doc;
      else delete next[sectionKey];
      return next;
    });
  };

  const handleAttachmentsChange = (sectionKey, op, item) => {
    setAttachments((prev) => {
      const next = { ...prev };
      const list = next[sectionKey] ? [...next[sectionKey]] : [];
      if (op === "add") {
        list.unshift(item);
      } else if (op === "delete") {
        const i = list.findIndex((x) => x.id === item.id);
        if (i >= 0) list.splice(i, 1);
      }
      if (list.length > 0) next[sectionKey] = list;
      else delete next[sectionKey];
      return next;
    });
  };

  const handleActionsChanged = (pid, updatedDoc) => {
    setRaw((prev) => {
      if (!prev) return prev;
      const list = Array.isArray(prev.action_plans) ? prev.action_plans : [];
      let next;
      if (updatedDoc === null) {
        next = list.filter((p) => p.id !== pid);
      } else {
        next = list.map((p) => (p.id === pid ? { ...p, ...updatedDoc } : p));
      }
      return { ...prev, action_plans: next };
    });
  };

  // Inject @page setup for printing — same approach as AEPReport
  useEffect(() => {
    const id_ = "aep-page-style";
    let style = document.getElementById(id_);
    if (!style) {
      style = document.createElement("style");
      style.id = id_;
      document.head.appendChild(style);
    }
    style.textContent = `@page { size: A4 ${orient}; margin: ${MARGIN_MM[margin]}; }`;
  }, [orient, margin]);

  /** Build the per-assessment-shaped `data` object that AEPSections expects.
   * We synthesize a "virtual assessment" representing the whole CNPJ so every
   * AEP section keeps working without a single conditional inside the section
   * components. */
  const adapted = useMemo(() => {
    if (!raw) return null;
    const company = raw.canonical || {};

    const titlesList = (raw.assessments || []).map((x) => x.titulo).filter(Boolean);
    const ghes = Array.from(new Set((raw.assessments || []).map((x) => x.ghe_setor).filter(Boolean))).join(", ");
    const funcs = Array.from(new Set((raw.assessments || []).map((x) => x.funcao).filter(Boolean))).join(", ");
    const matrixType = raw.assessments?.[0]?.matrix_type || "pxs";

    const a = {
      id: `cnpj-${raw.cnpj}`,
      titulo: `AEP Consolidado — CNPJ ${raw.cnpj_formatted || raw.cnpj}`,
      ghe_setor: ghes || "Consolidado por CNPJ",
      funcao: funcs || "",
      n_trabalhadores: raw.expected_workers || 0,
      // The technical responsible is user-editable per CNPJ via /aep-editor-config.
      // Falls back to the report generator's name when no override is set.
      responsavel: (signerConfig.technical_responsible && signerConfig.technical_responsible.trim())
        || raw.generated_by?.name
        || "",
      data_avaliacao: (raw.generated_at || "").slice(0, 10),
      observacoes: titlesList.length
        ? `Avaliações consolidadas (${titlesList.length}): ${titlesList.slice(0, 6).join("; ")}${titlesList.length > 6 ? "…" : ""}`
        : "",
      matrix_type: matrixType,
      company_snapshot: {
        id: company.id || "",
        razao_social: company.razao_social || "",
        nome_fantasia: company.nome_fantasia || "",
        cnpj: raw.cnpj_formatted || raw.cnpj,
        cnae: company.cnae || "",
        grau_risco: company.grau_risco || "",
        unidade: company.unidade || "",
        endereco: company.endereco || "",
        numero: company.numero || "",
        complemento: company.complemento || "",
        bairro: company.bairro || "",
        cidade: company.cidade || "",
        uf: company.uf || "",
        cep: company.cep || "",
        responsavel_tecnico: company.responsavel_tecnico || "",
        logo_base64: company.logo_base64 || "",
      },
      // Signatures aren't propagated from individual AEPs — keep blank.
      company_signature_base64: "",
      technical_signature_base64: "",
      // Editable per CNPJ (via /aep-editor-config).
      company_signer: (signerConfig.company_representative && signerConfig.company_representative.trim()) || "",
      technical_signer: (signerConfig.technical_responsible && signerConfig.technical_responsible.trim()) || "",
    };

    const data = {
      assessment: a,
      dimensions: raw.dimensions || [],
      questions: raw.questions || [],
      overall_score: raw.overall_score || 0,
      overall: raw.overall || { level: 0, label: "Sem dados", key: "no_data" },
      response_count: raw.response_count || 0,
      critical_count: raw.critical_count || 0,
    };

    // === Unificar classificação Dashboard ↔ Matriz AIHA ===
    // Historicamente o Dashboard/Resumo mostravam a classe derivada só do
    // score médio (ex.: 54% = Moderado), enquanto a Matriz AIHA usa P × S
    // (que sobe pra ALTO quando há respostas críticas). Isso gerava textos
    // conflitantes ("Moderado" no dashboard e "ALTO" na matriz na mesma
    // página). Reclassificamos o overall usando exatamente as mesmas funções
    // da Matriz (fonte única de verdade) sempre que houver dados suficientes.
    if ((data.response_count || 0) > 0 && data.dimensions.length > 0) {
      const worstScore = Math.max(...data.dimensions.filter((d) => d.responses > 0).map((d) => d.score), 0);
      const p = deriveProbabilityIndex(data.overall_score);
      const s = deriveSeverityIndex(
        worstScore,
        data.critical_count || 0,
        data.response_count,
        data.overall_score,
        Boolean(raw.assessments?.[0]?.aiha_strict),
      );
      if (p && s) {
        const matrixLabel = matrixClassification(p, s); // "Baixo" | "Moderado" | "Alto" | "Crítico"
        const levelMap = { "Baixo": 1, "Moderado": 2, "Alto": 3, "Crítico": 4 };
        const keyMap = { "Baixo": "low", "Moderado": "moderate", "Alto": "high", "Crítico": "critical" };
        data.overall = {
          ...data.overall,
          label: matrixLabel,
          level: levelMap[matrixLabel] || data.overall.level,
          key: keyMap[matrixLabel] || data.overall.key,
          matrix_derived: true,
          matrix_p: p,
          matrix_s: s,
        };
      }
    }

    const actions = (raw.action_plans || []).map((p) => ({
      id: p.id,
      assessment_id: p.assessment_id,
      acao: p.acao,
      dimensao: p.dimensao || "",
      responsavel: p.responsavel || "",
      prazo: p.prazo || "",
      prioridade: p.prioridade || "Média",
      status: p.status || "Pendente",
    }));

    // "responses" lista os respondentes para a seção "Base importada e revisão
    // dos dados". O backend retorna apenas metadata leve (sem o payload completo
    // de answers), garantindo que a impressão funcione mesmo com centenas de
    // respostas. `answers.length` continua refletindo o tamanho real.
    const responses = (raw.responses || []).map((r) => ({
      id: r.id,
      employee_name: r.employee_name || "",
      employee_matricula: r.employee_matricula || "",
      submitted_at: r.submitted_at || "",
      answers: r.answers || [],
      // Preserve per-instrument metadata so AEPBase can render "22/22" for a
      // Teste Rápido response instead of falling back to "22/54" (NR-01 legacy).
      question_count: r.question_count || 0,
      test_name: r.test_name || "",
      assessment_titulo: r.assessment_titulo || "",
    }));

    const date = new Date(raw.generated_at || Date.now()).toLocaleDateString("pt-BR");
    const hash = `AEP-CNPJ-${date.replaceAll("/", "")}-${(raw.cnpj || "").slice(-6).toUpperCase()}`;
    const score100 = Math.round((data.overall_score / 5) * 100);
    const criticalCount = data.critical_count || data.questions.filter((q) => q.score >= 4.5).length;
    const ranked = [...data.dimensions]
      .filter((d) => d.responses > 0)
      .sort((x, y) => y.score - x.score);
    const focoDim = ranked[0]?.dimensao || "";
    const critByDim = {};
    data.questions.forEach((q) => {
      if (q.score >= 4) critByDim[q.dimensao] = (critByDim[q.dimensao] || 0) + 1;
    });
    const maxResp = Math.max(...ranked.map((d) => d.responses), 1);

    const derived = { a, company: a.company_snapshot, date, hash, score100, criticalCount, ranked, focoDim, critByDim, maxResp };

    // Same priority-bucket logic as useAEPData
    const buckets = { "30 dias": 0, "60 dias": 0, "90 dias": 0, "180 dias": 0 };
    actions.forEach((act) => {
      if (!act.prazo) return;
      const diff = (new Date(act.prazo) - new Date()) / 86400000;
      if (diff <= 30) buckets["30 dias"] += 1;
      else if (diff <= 60) buckets["60 dias"] += 1;
      else if (diff <= 90) buckets["90 dias"] += 1;
      else if (diff <= 180) buckets["180 dias"] += 1;
    });

    return { data, actions, responses, derived, timelineBuckets: buckets };
  }, [raw, signerConfig]);

  const triggerPrint = () => {
    if (isTrial) {
      toast.error("Geração de PDF disponível apenas no plano completo. Veja os planos para assinar agora.");
      api.post("/leads/upgrade-click", {
        event_type: "paywall_hit",
        context: `AEP CNPJ print attempt · ${cnpj}`,
      }).catch(() => {});
      setTimeout(() => { window.location.href = "/app/planos"; }, 1500);
      return;
    }
    setShowSetup(false);
    setTimeout(() => {
      try { window.print(); }
      catch { toast.error("Use Ctrl+P / Cmd+P para imprimir."); }
    }, 250);
  };

  if (!adapted) return <div className="p-12 text-slate-500">Carregando AEP consolidado…</div>;

  const { data, actions, responses, derived, timelineBuckets } = adapted;
  const { a, company, date, hash, score100, criticalCount, ranked, focoDim, critByDim, maxResp } = derived;
  const show = (k) => included[k];

  return (
    <div className="bg-white min-h-screen">
      <div className="no-print sticky top-0 z-50 bg-slate-900 text-white px-6 py-3 flex justify-between items-center gap-3 flex-wrap">
        <Link to={`/app/cnpj/${cnpj}/consolidado`} className="text-sm" data-testid="cnpj-aep-back">← Voltar</Link>
        <div className="text-sm">
          AEP Consolidado · {raw.assessment_count} avaliações · {orient === "paisagem" ? "Paisagem" : "Retrato"} ·{" "}
          {SECTIONS.filter((s) => included[s.k]).length}/{SECTIONS.length} seções
        </div>
        {included.matriz_consolidada && (
          <div className="flex items-center gap-1 text-xs bg-slate-800/80 px-2 py-1 rounded" data-testid="matriz-views-toggle">
            <span className="text-slate-300 mr-1">Matriz:</span>
            {[
              ["geral", "Geral"],
              ["ghe", "GHE"],
              ["setor", "Setor"],
              ["individual", "Individual"],
            ].map(([k, lbl]) => {
              const active = matrizViews.includes(k);
              return (
                <button
                  key={k}
                  data-testid={`matriz-view-${k}`}
                  onClick={() => setMatrizViews((v) =>
                    v.includes(k) ? v.filter((x) => x !== k) : [...v, k]
                  )}
                  className={`px-2 py-0.5 border text-[11px] font-bold uppercase tracking-wider ${
                    active
                      ? "bg-emerald-600 border-emerald-500 text-white"
                      : "bg-transparent border-slate-600 text-slate-400 hover:border-slate-400"
                  }`}>
                  {lbl}
                </button>
              );
            })}
          </div>
        )}
        <div className="flex gap-2 items-center flex-wrap">
          {!isTrial && (
            <label className="hidden md:flex items-center gap-2 text-[11px] text-slate-300 no-print" title="Nome que aparecerá como Responsável Técnico na capa, controle documental e assinaturas.">
              <span className="uppercase tracking-wider font-bold">Resp. técnico:</span>
              <input
                type="text"
                defaultValue={signerConfig.technical_responsible}
                key={`tr-${signerConfig.technical_responsible}`}
                onBlur={(e) => {
                  if (e.target.value !== signerConfig.technical_responsible) {
                    saveSignerField("technical_responsible", e.target.value);
                  }
                }}
                onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                placeholder="Administrador Sollute"
                className="bg-slate-800 border border-slate-600 focus:border-emerald-400 focus:outline-none rounded px-2 py-1 text-xs text-white min-w-[180px]"
                data-testid="cnpj-aep-signer-tech"
              />
            </label>
          )}
          {isTrial ? (
            <>
              <span className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 border border-amber-400/40 text-amber-200 text-xs font-bold uppercase tracking-wider" data-testid="cnpj-aep-trial-badge">
                <i className="fa-solid fa-lock"></i> Modo demonstração
              </span>
              <Link to="/app/planos" className="btn bg-[#8BC53F] text-emerald-950 hover:bg-white text-sm" data-testid="cnpj-aep-upgrade">
                <i className="fa-solid fa-tags"></i> Ver planos
              </Link>
            </>
          ) : (
            <>
              <button onClick={() => setShowSetup(true)} className="btn btn-secondary text-sm" data-testid="cnpj-aep-setup-button">
                <i className="fa-solid fa-sliders"></i> Configurar página
              </button>
              <Link to={`/aep/cnpj/${cnpj}/editor`} className="btn btn-secondary text-sm" data-testid="cnpj-aep-editor-link">
                <i className="fa-solid fa-pen-ruler"></i> Editor de páginas
              </Link>
              <Link to={`/aep/cnpj/${cnpj}/documento`} className="btn btn-secondary text-sm" data-testid="cnpj-aep-document-link">
                <i className="fa-solid fa-file-pen"></i> Editar documento
              </Link>
              <button onClick={triggerPrint} className="btn btn-primary text-sm" data-testid="cnpj-aep-print-button">
                <i className="fa-solid fa-print"></i> Imprimir / Salvar PDF
              </button>
            </>
          )}
        </div>
      </div>

      {isTrial && (
        <div className="no-print bg-amber-50 border-b border-amber-200 px-6 py-3 text-sm text-amber-900 flex flex-wrap items-center justify-between gap-3" data-testid="cnpj-aep-trial-banner">
          <div className="flex items-center gap-2">
            <i className="fa-solid fa-circle-info"></i>
            <span>Você está no <strong>plano Demonstração</strong>. A geração/impressão em PDF está disponível apenas no plano completo.</span>
          </div>
          <div className="flex gap-2 shrink-0">
            <Link to="/app/planos" className="btn btn-primary text-xs">
              <i className="fa-solid fa-tags"></i> Ver planos
            </Link>
            <a href={mailtoUrl()} className="btn btn-ghost text-xs"><i className="fa-solid fa-envelope"></i> E-mail</a>
            <a href={whatsappUrl()} target="_blank" rel="noreferrer" className="btn btn-ghost text-xs"><i className="fa-brands fa-whatsapp"></i> WhatsApp</a>
          </div>
        </div>
      )}

      <div className={`mx-auto aep-body ${ORIENT_CLASS[orient]} ${MARGIN_CLASS[margin]} ${FONT_CLASS[font]}`} lang="pt-BR">
        {show("capa")            && <AEPCover            a={a} company={company} date={date} hash={hash} />}
        {show("sumario")         && <AEPSummary          included={included} />}
        {show("dashboard")       && <AEPDashboard        data={data} score100={score100} criticalCount={criticalCount} />}
        {show("controle_doc")    && <AEPControleDoc      a={a} company={company} date={date} hash={hash} />}
        {show("resumo")          && <AEPResumoExec       data={data} score100={score100} criticalCount={criticalCount} focoDim={focoDim} />}
        {show("prioridades")     && <AEPPrioridades      ranked={ranked} />}
        {show("rastreabilidade") && <AEPRastreabilidade  ranked={ranked} cnpj={company?.cnpj} />}
        {show("heatmap")         && <AEPHeatmap          ranked={ranked} critByDim={critByDim} maxResp={maxResp} />}
        {show("timeline")        && <AEPTimeline         timelineBuckets={timelineBuckets} />}
        {show("checklist")       && <AEPChecklist        hasActions={actions.length > 0} />}
        {show("lgpd")            && <AEPLgpd />}
        {show("intro")           && <AEPIntro />}
        {show("base")            && <AEPBase             responses={responses} totalQuestions={data?.questions?.length} />}
        {show("consolidacao")    && <AEPConsolidacao     data={data} />}
        {show("matriz")          && <AEPMatriz           data={data} criticalCount={criticalCount} matrixType={a.matrix_type || "pxs"} expectedWorkers={a.n_trabalhadores || 0} />}
        {show("matriz_consolidada") && (
          <AEPMatrizConsolidada
            geral={{
              dimensions: data.dimensions,
              overall_score: data.overall_score,
              overall: data.overall,
              response_count: data.response_count,
              critical_count: criticalCount,
            }}
            byGhe={raw.by_ghe || []}
            perAssessment={raw.per_assessment || []}
            views={matrizViews}
            matrixType={a.matrix_type || "pxs"}
            expectedWorkers={a.n_trabalhadores || 0}
          />
        )}
        {show("plano")           && <AEPPlano            actions={actions} cnpj={cnpj} overrides={overrides} onOverrideChange={handleOverrideChange} onActionsChanged={handleActionsChanged} />}
        {show("recomendacoes_ia") && <AEPRecomendacoesIA  assessmentId={a?.id} assessmentIds={(raw?.assessments || []).map((x) => x.id).filter(Boolean)} cnpj={cnpj} overrides={overrides} onOverrideChange={handleOverrideChange} />}
        {show("plano_anual")     && <AEPPlanoAnual       companyId={company?.id || a?.company_id} cnpj={cnpj} overrides={overrides} onOverrideChange={handleOverrideChange} attachments={attachments} onAttachmentsChange={handleAttachmentsChange} />}
        {show("absenteismo")     && <AEPAbsenteismo      companyId={company?.id || a?.company_id} cnpj={cnpj} overrides={overrides} onOverrideChange={handleOverrideChange} attachments={attachments} onAttachmentsChange={handleAttachmentsChange} />}
        {show("conclusao")       && <AEPConclusao        data={data} score100={score100} criticalCount={criticalCount} cnpj={cnpj} overrides={overrides} onOverrideChange={handleOverrideChange} />}
        {show("assinaturas")     && <AEPAssinaturas      a={a} company={company} date={date} hash={hash} />}
      </div>

      {showSetup && (
        <AEPSetupModal
          orient={orient}     setOrient={setOrient}
          margin={margin}     setMargin={setMargin}
          font={font}         setFont={setFont}
          included={included} setIncluded={setIncluded}
          onClose={() => setShowSetup(false)}
          onPrint={triggerPrint}
        />
      )}
    </div>
  );
}
